// Utility function to get steps based on framework and preferences
export const getStepperSteps = (preferences = {}) => {
  const { automationTool, framework, programmingLanguage } = preferences;

  // Base steps that are common to all frameworks
  const baseSteps = [
    "Test Case Imported",
    "Generate Page Objects",
    "Generate Test Scripts",
    "Generate Configuration",
    "Generate Reports",
  ];

  // Framework-specific step variations
  const frameworkSteps = {
    // Playwright TypeScript specific steps
    playwright: [
      "Test Case Imported",
      "Introduce SUT",
      "Generate Locators",
      "Generate Page Objects",
      "Generate Test Files"
    ],

    // Selenium specific steps
    selenium: [
      "Test Case Imported",
      "Generate Page Objects",
      "Generate Test Scripts",
      "Generate Configuration",
      "Generate Test Data",
      "Generate Reports",
    ],

    // Cypress specific steps
    cypress: [
      "Test Case Imported",
      "Generate Feature File",
      "Generate Test Scripts",
      "Generate Fixtures",
      "Generate Configuration",
      "Generate Reports",
    ],

    // Robot Framework specific steps
    robot: [
      "Test Case Imported",
      "Generate Keywords",
      "Generate Test Cases",
      "Generate Variables",
      "Generate Reports",
    ],

    // ReqNRoll (formerly SpecFlow) specific steps
    reqnroll: [
      "Test Case Imported",
      "Feature File",
      "Page & DOM",
      "Locators",
      "Page Class",
      "Step Definitions",
    ],

    // SpecFlow specific steps (similar to ReqNRoll)
    specflow: [
      "Test Case Imported",
      "Feature File",
      "Page & DOM",
      "Selectors",
      "Page Class",
      "Test Scripts",
      "Execution",
    ],
  };

  // Language-specific variations
  const languageSteps = {
    typescript: [
      "Test Case Imported",
      "Introduce SUT",
      "Generate Locators",
      "Generate Page Objects",
      "Generate Test Files"
    ],

    java: [
      "Test Case Imported",
      "Generate Feature File",
      "Generate Page Objects",
      "Generate Test Scripts",
      "Generate Utilities",
      "Generate Configuration",
      "Generate Reports",
    ],

    csharp: [
      "Test Case Imported",
      "Feature File",
      "Page & DOM",
      "Locators",
      "Page Objects",
      "Test Scripts",
    ],

    python: [
      "Test Case Imported",
      "Generate Feature File",
      "Generate Page Objects",
      "Generate Test Scripts",
      "Generate Fixtures",
      "Generate Configuration",
      "Generate Reports",
    ],
  };

  // Determine steps based on selections with priority order:
  // 1. Framework-specific steps (highest priority)
  // 2. Language-specific steps
  // 3. Automation tool steps
  // 4. Base steps (fallback)

  let steps = baseSteps;

  // Check for framework first (highest priority)
  if (framework && frameworkSteps[framework]) {
    steps = frameworkSteps[framework];
  }
  // Then check for automation tool
  else if (automationTool && frameworkSteps[automationTool]) {
    steps = frameworkSteps[automationTool];
  }
  // Finally check for language-specific variations
  else if (programmingLanguage && languageSteps[programmingLanguage]) {
    steps = languageSteps[programmingLanguage];
  }

  // Special handling for specific combinations
  if (automationTool === "playwright" && programmingLanguage === "typescript") {
    steps = languageSteps[programmingLanguage];
  }

  if (automationTool === "playwright" && programmingLanguage === "csharp") {
    steps = languageSteps[programmingLanguage];
  }

  // ReqNRoll + C# specific combination
  if (framework === "reqnroll" && programmingLanguage === "csharp") {
    steps = frameworkSteps.reqnroll;
  }

  // Add framework-specific additional steps
  if (framework) {
    switch (framework) {
      case "cucumber":
        if (!steps.includes("Generate Step Definitions")) {
          steps.splice(-1, 0, "Generate Step Definitions");
        }
        break;
      case "specflow":
      case "reqnroll":
        // ReqNRoll and SpecFlow have similar patterns
        if (
          !steps.includes("Step Definitions") &&
          !steps.includes("Test Scripts")
        ) {
          // Step definitions are typically handled in the Test Scripts phase
        }
        break;
      case "behave":
        if (!steps.includes("Generate Step Definitions")) {
          steps.splice(-1, 0, "Generate Step Definitions");
        }
        break;
      default:
        break;
    }
  }

  return steps;
};

// Get route mapping for specific framework/preferences
export const getRouteStepMapping = (preferences = {}) => {
  const { automationTool, framework, programmingLanguage } = preferences;

  const ROUTE_STEP_MAPPINGS = {
    reqnroll: {
      "test-case-imported": 0,
      "feature-file": 1,
      "page-dom": 2,
      locators: 3,
      "page-class": 4,
      "step-definition": 5,
    },
    specflow: {
      "test-case-imported": 0,
      "feature-file": 1,
      "page-dom": 2,
      locators: 3,
      "page-class": 4,
      "step-definition": 5,
    },
    playwright: {
      "test-case-imported": 0,
      "introduce-sut": 1,
      "generate-locators": 2,
      "generate-page-objects": 3,
      "generate-test-files": 4,
      "generate-project": 5,
    },
    selenium: {
      "test-case-imported": 0,
      "generate-page-objects": 1,
      "generate-test-scripts": 2,
      "generate-configuration": 3,
      "generate-test-data": 4,
      "generate-reports": 5,
    },
    cypress: {
      "test-case-imported": 0,
      "generate-feature-file": 1,
      "generate-test-scripts": 2,
      "generate-fixtures": 3,
      "generate-configuration": 4,
      "generate-reports": 5,
    },
    robot: {
      "test-case-imported": 0,
      "generate-keywords": 1,
      "generate-test-cases": 2,
      "generate-variables": 3,
      "generate-reports": 4,
    },
    // Language-based mappings
    csharp: {
      "test-case-imported": 0,
      "feature-file": 1,
      "page-dom": 2,
      locators: 3,
      "page-class": 4,
      "step-definition": 5,
      execution: 6,
    },
    java: {
      "test-case-imported": 0,
      "generate-feature-file": 1,
      "generate-page-objects": 2,
      "generate-test-scripts": 3,
      "generate-utilities": 4,
      "generate-configuration": 5,
      "generate-reports": 6,
    },
    typescript: {
      "test-case-imported": 0,
      "introduce-sut": 1,
      "generate-locators": 2,
      "generate-page-objects": 3,
      "generate-test-files": 4,
      "generate-project": 5,
    },
    python: {
      "test-case-imported": 0,
      "generate-feature-file": 1,
      "generate-page-objects": 2,
      "generate-test-scripts": 3,
      "generate-fixtures": 4,
      "generate-configuration": 5,
      "generate-reports": 6,
    },
  };

  // Determine mapping based on preferences with priority order:
  // 1. Framework first (highest priority)
  // 2. Automation tool
  // 3. Programming language
  // 4. Default to reqnroll mapping

  if (framework && ROUTE_STEP_MAPPINGS[framework]) {
    return ROUTE_STEP_MAPPINGS[framework];
  }

  if (automationTool && ROUTE_STEP_MAPPINGS[automationTool]) {
    return ROUTE_STEP_MAPPINGS[automationTool];
  }

  if (programmingLanguage && ROUTE_STEP_MAPPINGS[programmingLanguage]) {
    return ROUTE_STEP_MAPPINGS[programmingLanguage];
  }

  // Default fallback
  return ROUTE_STEP_MAPPINGS.reqnroll;
};

// Get step index by route using preferences
export const getStepIndexByRoute = (route, preferences = {}) => {
  const mapping = getRouteStepMapping(preferences);
  return mapping[route] !== undefined ? mapping[route] : -1;
};

// Get current step from URL and preferences
export const getCurrentStepFromRoute = (pathname, preferences = {}) => {
  // Extract current route segment
  const routeSegments = pathname.split("/").filter(Boolean);
  const currentRoute = routeSegments[routeSegments.length - 1];

  // Get step index from route mapping
  let stepIndex = getStepIndexByRoute(currentRoute, preferences);

  // If no direct match, try fuzzy matching with steps
  if (stepIndex === -1) {
    const steps = getStepperSteps(preferences);
    stepIndex = getStepByRoute(steps, currentRoute);
  }

  // Ensure valid step index
  const steps = getStepperSteps(preferences);
  return Math.max(
    0,
    Math.min(stepIndex === -1 ? 0 : stepIndex, steps.length - 1)
  );
};

// Get step index by step name
export const getStepIndex = (steps, stepName) => {
  return steps.findIndex((step) => step === stepName);
};

// Get next step name
export const getNextStep = (steps, currentIndex) => {
  return currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
};

// Get previous step name
export const getPreviousStep = (steps, currentIndex) => {
  return currentIndex > 0 ? steps[currentIndex - 1] : null;
};

// Check if step is completed
export const isStepCompleted = (currentIndex, targetIndex) => {
  return currentIndex > targetIndex;
};

// Check if step is active
export const isStepActive = (currentIndex, targetIndex) => {
  return currentIndex === targetIndex;
};

// Check if step is pending
export const isStepPending = (currentIndex, targetIndex) => {
  return currentIndex < targetIndex;
};

// Get step status
export const getStepStatus = (currentIndex, targetIndex) => {
  if (currentIndex === targetIndex) return "active";
  if (currentIndex > targetIndex) return "completed";
  return "pending";
};

// Normalize route name for matching
export const normalizeRouteName = (route) => {
  return route.toLowerCase().replace(/-/g, " ").trim();
};

// Get step by route with fuzzy matching
export const getStepByRoute = (steps, route) => {
  const normalizedRoute = normalizeRouteName(route);

  // Direct step name matching
  const directMatch = steps.find(
    (step) => step.toLowerCase() === normalizedRoute
  );

  if (directMatch) {
    return steps.indexOf(directMatch);
  }

  // Partial matching
  const partialMatch = steps.find((step) => {
    const normalizedStep = step.toLowerCase();
    return (
      normalizedStep.includes(normalizedRoute) ||
      normalizedRoute.includes(normalizedStep.split(" ")[0])
    );
  });

  return partialMatch ? steps.indexOf(partialMatch) : -1;
};

// Get all available frameworks
export const getAvailableFrameworks = () => {
  return ["reqnroll", "specflow", "playwright", "selenium", "cypress", "robot"];
};

// Get all available programming languages
export const getAvailableLanguages = () => {
  return ["csharp", "java", "typescript", "python"];
};

// Get all available automation tools
export const getAvailableAutomationTools = () => {
  return ["playwright", "selenium", "cypress", "robot"];
};

// Validate preferences
export const validatePreferences = (preferences) => {
  const { automationTool, framework, programmingLanguage } = preferences;

  const availableFrameworks = getAvailableFrameworks();
  const availableLanguages = getAvailableLanguages();
  const availableTools = getAvailableAutomationTools();

  const errors = [];

  if (framework && !availableFrameworks.includes(framework)) {
    errors.push(
      `Invalid framework: ${framework}. Available: ${availableFrameworks.join(
        ", "
      )}`
    );
  }

  if (
    programmingLanguage &&
    !availableLanguages.includes(programmingLanguage)
  ) {
    errors.push(
      `Invalid language: ${programmingLanguage}. Available: ${availableLanguages.join(
        ", "
      )}`
    );
  }

  if (automationTool && !availableTools.includes(automationTool)) {
    errors.push(
      `Invalid tool: ${automationTool}. Available: ${availableTools.join(", ")}`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Get step progress percentage
export const getStepProgress = (currentStep, totalSteps) => {
  if (totalSteps === 0) return 0;
  return Math.round(((currentStep + 1) / totalSteps) * 100);
};

// Get remaining steps
export const getRemainingSteps = (steps, currentIndex) => {
  return steps.slice(currentIndex + 1);
};

// Get completed steps
export const getCompletedSteps = (steps, currentIndex) => {
  return steps.slice(0, currentIndex);
};

// Check if workflow is complete
export const isWorkflowComplete = (currentIndex, totalSteps) => {
  return currentIndex >= totalSteps - 1;
};

// Get step navigation info
export const getStepNavigationInfo = (steps, currentIndex) => {
  return {
    currentStep: steps[currentIndex] || "Unknown",
    nextStep: getNextStep(steps, currentIndex),
    previousStep: getPreviousStep(steps, currentIndex),
    progress: getStepProgress(currentIndex, steps.length),
    isFirst: currentIndex === 0,
    isLast: currentIndex >= steps.length - 1,
    remainingSteps: getRemainingSteps(steps, currentIndex),
    completedSteps: getCompletedSteps(steps, currentIndex),
    totalSteps: steps.length,
  };
};

// Get route URL from step index and preferences
export const getRouteFromStepIndex = (stepIndex, preferences = {}) => {
  const mapping = getRouteStepMapping(preferences);

  // Find the route that matches the step index
  const routeEntry = Object.entries(mapping).find(
    ([route, index]) => index === stepIndex
  );

  return routeEntry ? routeEntry[0] : null;
};

// Get all routes for a specific framework/preferences
export const getAllRoutes = (preferences = {}) => {
  const mapping = getRouteStepMapping(preferences);
  return Object.keys(mapping).sort((a, b) => mapping[a] - mapping[b]);
};

// Generate navigation URLs for all steps
export const generateStepNavigationUrls = (basePath, preferences = {}) => {
  const routes = getAllRoutes(preferences);
  const steps = getStepperSteps(preferences);

  return steps.map((step, index) => {
    const route = getRouteFromStepIndex(index, preferences);
    return route ? `${basePath}/${route}` : null;
  });
};

// Create step click handler factory
export const createStepNavigationHandler = (
  navigate,
  basePath,
  preferences = {}
) => {
  return (stepIndex, stepName) => {
    const route = getRouteFromStepIndex(stepIndex, preferences);
    if (route && navigate) {
      const fullPath = `${basePath}/${route}`;
      console.log(
        `Navigating to step ${stepIndex + 1}: ${stepName} -> ${fullPath}`
      );
      navigate(fullPath);
    }
  };
};

// Get step accessibility info
export const getStepAccessibilityInfo = (
  stepIndex,
  currentStepIndex,
  steps
) => {
  const stepName = steps[stepIndex];
  const status = getStepStatus(currentStepIndex, stepIndex);

  return {
    "aria-label": `Step ${stepIndex + 1} of ${steps.length}: ${stepName}`,
    "aria-current": status === "active" ? "step" : undefined,
    "aria-disabled": status === "pending",
    role: "button",
    tabIndex: status !== "pending" ? 0 : -1,
  };
};

// Validate step navigation
export const canNavigateToStep = (
  targetStepIndex,
  currentStepIndex,
  navigationPolicy = "completedAndNext"
) => {
  switch (navigationPolicy) {
    case "all":
      return true;
    case "completed":
      return targetStepIndex < currentStepIndex;
    case "completedAndNext":
      return targetStepIndex <= currentStepIndex + 1;
    case "completedAndCurrent":
      return targetStepIndex <= currentStepIndex;
    case "none":
      return false;
    default:
      if (Array.isArray(navigationPolicy)) {
        return navigationPolicy.includes(targetStepIndex);
      }
      return false;
  }
};

// Get clickable step indices
export const getClickableSteps = (
  currentStepIndex,
  totalSteps,
  policy = "completedAndNext"
) => {
  const clickableSteps = [];

  for (let i = 0; i < totalSteps; i++) {
    if (canNavigateToStep(i, currentStepIndex, policy)) {
      clickableSteps.push(i);
    }
  }

  return clickableSteps;
};
